﻿<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import type { CategoryRecord, ImageDraft, ImageMetaRecord } from '../types/catalog'
import { getImageBlob } from '../services/catalogService'
import { formatBytes, formatDate } from '../utils/format'
import { parseTags } from '../utils/text'
import AppDialog from './AppDialog.vue'
import AppIcon from './AppIcon.vue'
import CategorySelect from './CategorySelect.vue'

const props = defineProps<{
  open: boolean
  image: ImageMetaRecord | null
  categories: CategoryRecord[]
  busy: boolean
}>()

const emit = defineEmits<{
  close: []
  save: [draft: ImageDraft]
  remove: []
  rotate: [degrees: 90 | -90 | 180]
}>()

const title = ref('')
const categoryId = ref<string | null>(null)
const description = ref('')
const tagsText = ref('')
const previewUrl = ref('')
const previewLoading = ref(false)
let loadVersion = 0

function clearPreview(): void {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = ''
}

async function loadPreview(): Promise<void> {
  const image = props.image
  if (!image) return
  const version = ++loadVersion
  clearPreview()
  previewLoading.value = true
  try {
    const blob = await getImageBlob(image.id, 'preview')
    if (blob && version === loadVersion) previewUrl.value = URL.createObjectURL(blob)
  } finally {
    if (version === loadVersion) previewLoading.value = false
  }
}

watch([() => props.open, () => props.image?.id], ([open]) => {
  if (!open || !props.image) {
    loadVersion += 1
    clearPreview()
    return
  }
  title.value = props.image.title
  categoryId.value = props.image.categoryId
  description.value = props.image.description
  tagsText.value = props.image.tags.join(', ')
  void loadPreview()
}, { immediate: true })

function save(): void {
  if (!categoryId.value) return
  emit('save', {
    title: title.value,
    categoryId: categoryId.value,
    description: description.value,
    tags: parseTags(tagsText.value)
  })
}

onBeforeUnmount(() => {
  loadVersion += 1
  clearPreview()
})
</script>

<template>
  <AppDialog
    :open="open"
    title="Edit image"
    wide
    full-screen-mobile
    :close-disabled="busy"
    @close="emit('close')"
  >
    <template #eyebrow>Details and content</template>

    <div v-if="image" class="editor-layout">
      <div class="editor-preview-column">
        <div class="editor-preview">
          <img v-if="previewUrl" :src="previewUrl" :alt="image.title" />
          <span v-else-if="previewLoading" class="spinner" />
        </div>
        <div class="editor-image-facts">
          <span>{{ image.width }} × {{ image.height }} px</span>
          <span>{{ formatBytes(image.size) }}</span>
          <span>{{ formatDate(image.createdAt) }}</span>
        </div>
        <div class="editor-rotate-actions">
          <button type="button" class="button button--secondary button--compact" :disabled="busy" @click="emit('rotate', -90)">
            <AppIcon name="rotateLeft" :size="17" /> Rotate left
          </button>
          <button type="button" class="button button--secondary button--compact" :disabled="busy" @click="emit('rotate', 90)">
            <AppIcon name="rotateRight" :size="17" /> Rotate right
          </button>
        </div>
      </div>

      <div class="editor-form">
        <label class="field">
          <span>Title</span>
          <input v-model="title" type="text" maxlength="160" required />
        </label>
        <CategorySelect v-model="categoryId" :categories="categories" required />
        <label class="field">
          <span>Description</span>
          <textarea v-model="description" rows="5" placeholder="Context, notes or searchable details" />
        </label>
        <label class="field">
          <span>Tag <small>comma separated</small></span>
          <input v-model="tagsText" type="text" placeholder="family, travel, receipt" />
        </label>
        <div class="source-file-box">
          <span>Original file</span>
          <strong>{{ image.originalName }}</strong>
          <small>{{ image.mimeType }}</small>
        </div>
      </div>
    </div>

    <template #footer>
      <button type="button" class="button button--danger-ghost" :disabled="busy" @click="emit('remove')">
        <AppIcon name="trash" :size="18" /> Delete
      </button>
      <span class="dialog-footer-spacer" />
      <button type="button" class="button button--ghost" :disabled="busy" @click="emit('close')">Cancel</button>
      <button type="button" class="button button--primary" :disabled="busy || !categoryId || !title.trim()" @click="save">
        <span v-if="busy" class="spinner spinner--small" />
        <AppIcon v-else name="check" :size="18" /> Save
      </button>
    </template>
  </AppDialog>
</template>
